import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Image as ImageIcon, FileText, Copy, Trash2, Search, X, Check, Upload } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";

const API = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (u && u.startsWith("http") ? u : `${API}${u}`);

/**
 * MediaLibrary — grid of previously uploaded files.
 * If `onPick` is passed, items are selectable and this behaves as a picker (used inside ImageUpload).
 */
export default function MediaLibrary({ onPick = null, kindDefault = "images" }) {
  const [files, setFiles] = useState([]);
  const [kind, setKind] = useState(kindDefault);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadUrl, setUploadUrl] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/admin/files", { params: { kind, search: search || undefined, limit: 200 } })
      .then(r => setFiles(r.data))
      .catch(() => setFiles([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [kind, search]); // eslint-disable-line react-hooks/exhaustive-deps

  const copy = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("URL copied"); }
    catch { toast.error("Copy failed"); }
  };

  const del = async (id) => {
    if (!confirm("Remove this file from the library? Existing article references keep working.")) return;
    try { await api.delete(`/admin/files/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="media-library">
      <div className="card-soft p-6 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px]">
          <div className="overline text-sky-700 mb-1">Media Library</div>
          <h3 className="font-editorial text-xl font-bold text-slate-900">All uploaded images & documents</h3>
          <p className="text-sm text-slate-600 mt-1">Click any image URL to copy and paste into an article, or use "Pick from library" inside any image field.</p>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 focus-within:border-sky-400">
          <Search className="w-4 h-4 text-slate-400"/>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search filename…" className="outline-none text-sm bg-transparent" data-testid="media-search"/>
        </div>
        <select value={kind} onChange={e => setKind(e.target.value)} className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm" data-testid="media-kind">
          <option value="all">All files</option>
          <option value="images">Images only</option>
          <option value="docs">Documents (PDF/CV)</option>
        </select>
        {!onPick && (
          <button onClick={() => setUploadOpen(v => !v)} className="px-5 py-2.5 rounded-full bg-sky-700 hover:bg-sky-800 text-white text-sm font-semibold flex items-center gap-1" data-testid="media-upload-btn"><Upload className="w-4 h-4"/>Upload new</button>
        )}
      </div>

      {uploadOpen && !onPick && (
        <div className="card-soft p-6">
          <ImageUpload label="Upload to library" value={uploadUrl} onChange={(v) => { setUploadUrl(v); load(); }} testId="media-lib-uploader" height="h-40"/>
          {uploadUrl && <p className="mt-3 text-xs text-emerald-700 flex items-center gap-1"><Check className="w-3 h-3"/>Uploaded — it now appears in the grid below.</p>}
        </div>
      )}

      <div className="card-soft p-4" data-testid="media-grid">
        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm">Loading…</div>
        ) : files.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">No files match. Upload an image to any article or press-release to populate the library.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {files.map(f => {
              const isImg = f.content_type && f.content_type.startsWith("image/");
              const url = resolveUrl(f.url);
              return (
                <div key={f.id} className="group relative rounded-2xl overflow-hidden border border-slate-100 bg-slate-50" data-testid={`media-item-${f.id}`}>
                  {isImg ? (
                    <img src={url} alt={f.original_filename || ""} className="w-full h-32 object-cover" loading="lazy"/>
                  ) : (
                    <div className="w-full h-32 grid place-items-center text-slate-400"><FileText className="w-8 h-8"/></div>
                  )}
                  <div className="p-2 text-[10px] text-slate-500 truncate" title={f.original_filename}>{f.original_filename || "unnamed"}</div>
                  <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-2">
                    {onPick ? (
                      <button onClick={() => onPick(f.url)} className="px-3 py-1.5 rounded-full bg-white text-slate-900 text-xs font-bold" data-testid={`media-pick-${f.id}`}>Use this</button>
                    ) : (
                      <>
                        <button onClick={() => copy(url)} className="px-3 py-1.5 rounded-full bg-white text-slate-900 text-xs font-bold flex items-center gap-1" data-testid={`media-copy-${f.id}`}><Copy className="w-3 h-3"/>Copy URL</button>
                        <a href={url} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-full bg-white/90 text-slate-900 text-xs font-semibold">Open</a>
                        <button onClick={() => del(f.id)} className="px-3 py-1.5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center gap-1" data-testid={`media-del-${f.id}`}><Trash2 className="w-3 h-3"/>Delete</button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * MediaPickerModal — opens the MediaLibrary in "picker" mode.
 * Used by ImageUpload as an alternative to upload.
 */
export function MediaPickerModal({ onPick, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-start pt-16 p-4 overflow-y-auto" onClick={onClose} data-testid="media-picker-modal">
      <div className="bg-white rounded-3xl max-w-6xl w-full p-6 relative max-h-[85vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100 z-10"><X className="w-4 h-4"/></button>
        <div className="overline text-sky-700 mb-1">Pick from library</div>
        <h2 className="font-editorial text-2xl font-bold text-slate-900 mb-4">Reuse a previously uploaded image</h2>
        <MediaLibrary onPick={(url) => { onPick(url); onClose(); }}/>
      </div>
    </div>
  );
}
